import { AfterViewInit, Component, ElementRef, EventEmitter, OnInit, Output, ViewChild, ViewEncapsulation, computed, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';

import { ChatService } from './agent.service';
import { isRuleRunThread } from './thread.service';
import { shortDateTime } from '../../shared/utils/format-datetime';

/**
 * Paleta de búsqueda de conversaciones (estilo Cmd+K de Claude desktop):
 * overlay centrado sobre toda la app con las conversaciones EN GRANDE,
 * filtrado en vivo y navegación por teclado (↑/↓, Enter abre, Esc cierra).
 * La abre la lupa de la cabecera del sidebar.
 */
@Component({
  selector: 'sg-thread-search-overlay',
  standalone: true,
  imports: [CommonModule, FormsModule, TranslatePipe],
  encapsulation: ViewEncapsulation.None,
  template: `
    <div class="sg-thread-search-mask" (click)="closed.emit()">
      <div class="sg-thread-search" (click)="$event.stopPropagation()" (keydown)="onKeydown($event)">
        <div class="sg-thread-search__input-row">
          <i class="pi pi-search" aria-hidden="true"></i>
          <input #searchInput
            type="text"
            [placeholder]="'agent.searchConversations' | translate"
            [ngModel]="query()"
            (ngModelChange)="onQueryChange($event)" />
          <button type="button" class="sg-thread-search__close"
            (click)="closed.emit()"
            [attr.aria-label]="'common.cancel' | translate">
            <i class="pi pi-times"></i>
          </button>
        </div>

        <div class="sg-thread-search__list" role="listbox">
          <div *ngFor="let thread of filtered(); let i = index; trackBy: trackByThread"
            #resultRow
            role="option"
            class="sg-thread-search__row"
            [class.selected]="i === selectedIndex()"
            [attr.aria-selected]="i === selectedIndex()"
            (mouseenter)="selectedIndex.set(i)"
            (click)="openThread(thread)">
            <i class="pi sg-thread-search__row-icon"
              [ngClass]="rowIcon(thread)"></i>
            <span class="sg-thread-search__row-title">{{ displayTitle(thread) }}</span>
            <span class="sg-thread-search__row-date">{{ dateLabel(thread) }}</span>
            <span class="sg-thread-search__row-enter" *ngIf="i === selectedIndex()">Enter</span>
          </div>

          <div class="sg-thread-search__empty" *ngIf="!filtered().length">
            <i class="pi pi-search"></i>
            <p>{{ 'agent.noSearchResults' | translate }}</p>
          </div>
        </div>
      </div>
    </div>
  `,
  styleUrls: ['./thread-search-overlay.component.scss']
})
export class ThreadSearchOverlayComponent implements OnInit, AfterViewInit {
  @Output() closed = new EventEmitter<void>();

  @ViewChild('searchInput')
  private searchInput?: ElementRef<HTMLInputElement>;

  readonly query = signal('');
  readonly selectedIndex = signal(0);

  readonly filtered = computed(() => {
    const threads = this.chatService.threads();
    const q = this.query().trim().toLowerCase();
    if (!q) return threads;
    return threads.filter(t =>
      t.title?.toLowerCase().includes(q) ||
      t.thread_id.toLowerCase().includes(q)
    );
  });

  constructor(
    private readonly chatService: ChatService,
    private readonly router: Router,
    private readonly translate: TranslateService
  ) {}

  ngOnInit(): void {
    // El listado suele estar ya en memoria (lo carga el rail); refrescar en
    // segundo plano por si hay títulos autogenerados nuevos.
    void this.chatService.listAllThreads();
  }

  ngAfterViewInit(): void {
    this.searchInput?.nativeElement.focus();
  }

  onQueryChange(value: string): void {
    this.query.set(value);
    this.selectedIndex.set(0);
  }

  onKeydown(event: KeyboardEvent): void {
    switch (event.key) {
      case 'Escape':
        event.preventDefault();
        this.closed.emit();
        break;
      case 'ArrowDown':
        event.preventDefault();
        this.moveSelection(1);
        break;
      case 'ArrowUp':
        event.preventDefault();
        this.moveSelection(-1);
        break;
      case 'Enter': {
        event.preventDefault();
        const thread = this.filtered()[this.selectedIndex()];
        if (thread) this.openThread(thread);
        break;
      }
    }
  }

  private moveSelection(delta: number): void {
    const count = this.filtered().length;
    if (!count) return;
    const next = (this.selectedIndex() + delta + count) % count;
    this.selectedIndex.set(next);
    // Mantener la fila seleccionada a la vista al navegar con el teclado
    const rows = document.querySelectorAll('.sg-thread-search__row');
    rows[next]?.scrollIntoView({ block: 'nearest' });
  }

  openThread(thread: any): void {
    void this.router.navigate(['/agent'], { queryParams: { thread_id: thread.thread_id } });
    this.closed.emit();
  }

  rowIcon(thread: any): string {
    if (this.chatService.isThreadRunning(thread.thread_id)) return 'pi-spinner pi-spin is-streaming';
    // La pausa, solo en conversaciones de ALERTA: en un chat normal el estado
    // `interrupted` también lo deja el detener la ejecución a mano, y eso no es
    // «esperando decisión» (mismo criterio que el rail y el historial).
    if (isRuleRunThread(thread)
        && (this.chatService.isThreadAwaitingDecision(thread.thread_id) || thread.status === 'interrupted')) {
      return 'pi-pause-circle is-awaiting';
    }
    return 'pi-comment';
  }

  /** Mismo criterio de título que el listado del rail (sg-agent-thread-list). */
  displayTitle(thread: any): string {
    const title = thread.title || thread.metadata?.title || '';
    if (title && !this.chatService.isDefaultThreadTitle(title)) return title;
    if (thread.metadata?.origin === 'alerts-agent-batch') {
      return this.translate.instant('agent.ruleRunThread');
    }
    return title || `Thread ${thread.thread_id.substring(0, 8)}`;
  }

  dateLabel(thread: any): string {
    const date = thread.updated_at || thread.created_at;
    if (!date) return '';
    return shortDateTime(date, this.translate.currentLang, {
      today: this.translate.instant('common.time.today'),
      yesterday: this.translate.instant('common.time.yesterday'),
    });
  }

  trackByThread(_: number, thread: any): string {
    return thread.thread_id;
  }
}
